/**
 * Keeps the free-shipping bar in step with the cart.
 *
 * Horizon dispatches `shopify:cart:lines-update` on document for every add,
 * change and remove. The event's own payload is not always a full cart, so this
 * re-reads /cart.js rather than trusting the detail — one small request per
 * cart mutation, deduped so a burst of quantity clicks does not fan out.
 */
class TreviiaShipBar extends HTMLElement {
  connectedCallback() {
    this.threshold = parseInt(this.dataset.threshold || '0', 10);
    this.textEl = this.querySelector('[data-tv-shipbar-text]');
    this.fillEl = this.querySelector('[data-tv-shipbar-fill]');
    if (!this.threshold || !this.textEl || !this.fillEl) return;

    this.onCartUpdate = this.onCartUpdate.bind(this);
    document.addEventListener('shopify:cart:lines-update', this.onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:cart:lines-update', this.onCartUpdate);
  }

  onCartUpdate() {
    // Collapse a burst of updates into one fetch.
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.refresh(), 120);
  }

  async refresh() {
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      // A rate-limited storefront answers 429 with an HTML body, which would
      // throw inside json() and look like a broken cart. Check the status.
      if (!res.ok) return;
      const cart = await res.json();
      this.render(cart.total_price ?? 0);
    } catch {
      /* leave the last good state on screen */
    }
  }

  money(cents) {
    // Matches money_without_trailing_zeros in the Liquid that renders the
    // initial state, so the bar does not change shape on its first update.
    const whole = cents % 100 === 0;
    const amount = (cents / 100).toLocaleString('en-EG', {
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: 2,
    });
    return `EGP ${amount}`;
  }

  render(total) {
    const remaining = this.threshold - total;
    const pct = Math.max(0, Math.min(100, (total / this.threshold) * 100));

    this.fillEl.style.width = `${pct}%`;

    if (remaining > 0) {
      this.textEl.innerHTML =
        total > 0
          ? `Add <strong>${this.money(remaining)}</strong> more for free shipping`
          : `Free shipping over <strong>${this.money(this.threshold)}</strong>`;
      this.classList.remove('is-unlocked');
    } else {
      this.textEl.innerHTML = `<strong>Free shipping unlocked</strong> — nice one`;
      this.classList.add('is-unlocked');
    }
  }
}

if (!customElements.get('tv-shipbar')) customElements.define('tv-shipbar', TreviiaShipBar);
